import { useEffect, useState } from "react";
import { StickyNote, Pencil, Trash2 } from "lucide-react";
import { adminApi } from "../lib/adminApi";

const stamp = (n) => {
  const created = new Date(n.createdAt).toLocaleString();
  return n.updatedAt !== n.createdAt
    ? `${created} · edited ${new Date(n.updatedAt).toLocaleString()}`
    : created;
};

const NotesPanel = ({ parentType, parentId }) => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/notes", { params: { parentType, parentId } })
      .then((res) => setNotes(res.data.items))
      .catch(() => {});
  }, [parentType, parentId]);

  const addNote = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const res = await adminApi.post("/admin/notes", { parentType, parentId, text });
      setNotes((prev) => [res.data, ...prev]);
      setDraft("");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const res = await adminApi.put(`/admin/notes/${id}`, { text });
      setNotes((prev) => prev.map((n) => (n.id === id ? res.data : n)));
      setEditingId(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await adminApi.delete(`/admin/notes/${id}`);
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <div className="mt-4 border-t border-border pt-4" data-testid={`notes-panel-${parentId}`}>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-grey">
        <StickyNote className="h-3.5 w-3.5 text-brand-red" aria-hidden="true" />
        Private Notes ({notes.length})
      </p>
      <div className="mt-3 flex gap-2">
        <textarea
          rows={2}
          className="field-input flex-1 resize-y text-sm"
          placeholder="Add an internal note (visible to administrators only)…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid={`note-input-${parentId}`}
        />
        <button
          type="button"
          className="btn-primary self-start px-4 py-2 text-xs disabled:opacity-50"
          onClick={addNote}
          disabled={!draft.trim() || busy}
          data-testid={`note-add-button-${parentId}`}
        >
          Add Note
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="border border-border bg-white p-3" data-testid={`note-item-${n.id}`}>
            {editingId === n.id ? (
              <div>
                <textarea
                  rows={2}
                  className="field-input w-full resize-y text-sm"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  data-testid={`note-edit-input-${n.id}`}
                />
                <div className="mt-2 flex gap-2">
                  <button type="button" className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50" disabled={!editText.trim() || busy} onClick={() => saveEdit(n.id)} data-testid={`note-save-${n.id}`}>
                    Save
                  </button>
                  <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setEditingId(null)} data-testid={`note-cancel-${n.id}`}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-brand-graphite" data-testid={`note-text-${n.id}`}>
                  {n.text}
                </p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-neutral-400">{stamp(n)}</p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="p-1 text-brand-grey transition-colors hover:text-brand-graphite"
                      onClick={() => { setEditingId(n.id); setEditText(n.text); }}
                      aria-label="Edit note"
                      data-testid={`note-edit-${n.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="p-1 text-brand-grey transition-colors hover:text-brand-red"
                      onClick={() => remove(n.id)}
                      aria-label="Delete note"
                      data-testid={`note-delete-${n.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotesPanel;
